import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/api";
import ToothChart, { CONDITIONS } from "@/components/ToothChart";
import { ArrowLeft, Save, Pill, Trash2, Plus, Printer } from "lucide-react";
import { toast } from "sonner";
import TreatmentPlans from "@/components/TreatmentPlans";
import { printPrescription } from "@/lib/printing";

const label = "text-xs font-semibold uppercase tracking-wider text-[#52525B] mb-1 block";
const input = "w-full bg-white border border-[#E5E5E2] text-sm px-3 py-2 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#0F4C3A] focus:border-[#0F4C3A]";

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
        active
          ? "border-[#0F4C3A] text-[#0F4C3A] font-semibold"
          : "border-transparent text-[#52525B] hover:text-[#1C1C1C]"
      }`}
    >
      {children}
    </button>
  );
}

export default function PatientDetail() {
  const { pid } = useParams();
  const [patient, setPatient] = useState(null);
  const [chart, setChart] = useState({ findings: [] });
  const [selected, setSelected] = useState([]);
  const [tab, setTab] = useState("emr");

  const [caseSheet, setCaseSheet] = useState({ chief_complaint: "", diagnosis: "", notes: "" });
  const [caseSheets, setCaseSheets] = useState([]);

  const [prescriptions, setPrescriptions] = useState([]);
  const [rxItems, setRxItems] = useState([{ drug: "", dose: "", frequency: "", duration: "", instructions: "" }]);
  const [rxAdvice, setRxAdvice] = useState("");

  const load = async () => {
    const [p, c, cs, rx] = await Promise.all([
      api.get(`/patients/${pid}`),
      api.get(`/patients/${pid}/chart`),
      api.get("/case-sheets", { params: { patient_id: pid } }),
      api.get("/prescriptions", { params: { patient_id: pid } }),
    ]);
    setPatient(p.data);
    setChart(c.data || { findings: [] });
    setCaseSheets(cs.data);
    setPrescriptions(rx.data);
  };
  useEffect(() => { load(); }, [pid]);

  const applyCondition = (cond) => {
    if (selected.length === 0) return toast.info("Select a tooth first");
    setChart((prev) => {
      const map = new Map(prev.findings.map((f) => [f.tooth, f]));
      selected.forEach((t) => {
        if (cond === "healthy") map.delete(t);
        else map.set(t, { tooth: t, condition: cond, surfaces: [], note: "" });
      });
      return { ...prev, findings: Array.from(map.values()) };
    });
    setSelected([]);
  };

  const toggleSelect = (t) =>
    setSelected((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));

  const saveCaseSheet = async () => {
    try {
      await api.post("/case-sheets", {
        patient_id: pid,
        chief_complaint: caseSheet.chief_complaint,
        diagnosis: caseSheet.diagnosis,
        notes: caseSheet.notes,
        findings: chart.findings,
      });
      toast.success("Case sheet saved");
      setCaseSheet({ chief_complaint: "", diagnosis: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addRxRow = () => setRxItems((r) => [...r, { drug: "", dose: "", frequency: "", duration: "", instructions: "" }]);
  const setRx = (i, k, v) => setRxItems((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const removeRx = (i) => setRxItems((r) => r.filter((_, idx) => idx !== i));

  const saveRx = async () => {
    const items = rxItems.filter((r) => r.drug && r.dose);
    if (items.length === 0) return toast.error("Add at least one drug");
    try {
      await api.post("/prescriptions", { patient_id: pid, items, advice: rxAdvice });
      toast.success("Prescription saved");
      setRxItems([{ drug: "", dose: "", frequency: "", duration: "", instructions: "" }]);
      setRxAdvice("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!patient) return <div className="p-10 text-sm text-[#52525B]">Loading…</div>;

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <Link to="/patients" className="inline-flex items-center gap-1 text-xs text-[#52525B] hover:text-[#1C1C1C] mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to patients
      </Link>

      {/* Patient header */}
      <div className="bg-white border border-[#E5E5E2] rounded-md p-6 mb-6">
        <div className="flex items-start justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#E27B58] font-semibold">{patient.mrn}</div>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-1" data-testid="patient-name">{patient.full_name}</h1>
            <div className="text-sm text-[#52525B] mt-1 space-x-4">
              <span className="capitalize">{patient.gender || "—"}</span>
              {patient.dob && <span>DOB {new Date(patient.dob).toLocaleDateString("en-IN")}</span>}
              <span className="font-mono-num">{patient.phone}</span>
              {patient.email && <span>{patient.email}</span>}
            </div>
          </div>
          <div className="text-right text-xs text-[#52525B]">
            <div>Registered</div>
            <div className="text-[#1C1C1C] font-medium">{new Date(patient.created_at).toLocaleDateString("en-IN")}</div>
          </div>
        </div>
        {(patient.medical_history || patient.allergies) && (
          <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-[#E5E5E2] text-sm">
            {patient.medical_history && <div><span className="text-xs uppercase tracking-wider text-[#52525B]">History</span><div>{patient.medical_history}</div></div>}
            {patient.allergies && <div><span className="text-xs uppercase tracking-wider text-[#D93838]">Allergies</span><div className="text-[#991B1B] font-medium">{patient.allergies}</div></div>}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-[#E5E5E2] mb-6 flex gap-2">
        <TabButton active={tab === "emr"} onClick={() => setTab("emr")} testid="tab-emr">EMR & Chart</TabButton>
        <TabButton active={tab === "rx"} onClick={() => setTab("rx")} testid="tab-rx">Prescriptions</TabButton>
        <TabButton active={tab === "plans"} onClick={() => setTab("plans")} testid="tab-plans">Treatment Plans</TabButton>
        <TabButton active={tab === "history"} onClick={() => setTab("history")} testid="tab-history">History</TabButton>
      </div>

      {tab === "emr" && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 bg-white border border-[#E5E5E2] rounded-md p-6">
            <h2 className="font-display text-lg font-bold tracking-tight mb-2">FDI Tooth Chart</h2>
            <p className="text-xs text-[#52525B] mb-4">Click teeth to select. Then apply a condition.</p>
            <div className="overflow-x-auto">
              <ToothChart findings={chart.findings} selected={selected} onSelect={toggleSelect} />
            </div>
            <div className="mt-6">
              <div className="text-xs uppercase tracking-wider text-[#52525B] mb-2">
                Apply to {selected.length || 0} selected {selected.length === 1 ? "tooth" : "teeth"}
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(CONDITIONS).map(([k, c]) => (
                  <button
                    key={k}
                    data-testid={`apply-${k}`}
                    onClick={() => applyCondition(k)}
                    className="text-xs px-3 py-1.5 rounded-sm border border-[#E5E5E2] hover:bg-[#F4F4F5] font-medium"
                    style={{ color: c.text === "#fff" ? c.border : c.text, borderColor: c.border }}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E5E5E2] rounded-md p-6">
            <h2 className="font-display text-lg font-bold tracking-tight mb-4">New Case Sheet</h2>
            <div className="space-y-3">
              <div>
                <label className={label}>Chief complaint</label>
                <input data-testid="cs-complaint" className={input} value={caseSheet.chief_complaint} onChange={(e) => setCaseSheet((s) => ({ ...s, chief_complaint: e.target.value }))} />
              </div>
              <div>
                <label className={label}>Diagnosis</label>
                <input data-testid="cs-diagnosis" className={input} value={caseSheet.diagnosis} onChange={(e) => setCaseSheet((s) => ({ ...s, diagnosis: e.target.value }))} />
              </div>
              <div>
                <label className={label}>Notes</label>
                <textarea rows={4} data-testid="cs-notes" className={input} value={caseSheet.notes} onChange={(e) => setCaseSheet((s) => ({ ...s, notes: e.target.value }))} />
              </div>
              <button data-testid="save-case-sheet" onClick={saveCaseSheet} className="w-full inline-flex items-center justify-center gap-2 bg-[#0F4C3A] text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-[#16654F]">
                <Save className="w-4 h-4" /> Save case sheet
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === "rx" && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 bg-white border border-[#E5E5E2] rounded-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-bold tracking-tight flex items-center gap-2"><Pill className="w-4 h-4 text-[#0F4C3A]" /> New Prescription</h2>
              <button onClick={addRxRow} className="text-xs px-3 py-1.5 border border-[#E5E5E2] rounded-sm hover:bg-[#F4F4F5] inline-flex items-center gap-1" data-testid="add-rx-row"><Plus className="w-3 h-3" /> Add drug</button>
            </div>
            <div className="space-y-2">
              {rxItems.map((r, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input placeholder="Drug" className={`${input} col-span-3`} value={r.drug} onChange={(e) => setRx(i, "drug", e.target.value)} data-testid={`rx-drug-${i}`} />
                  <input placeholder="Dose" className={`${input} col-span-2`} value={r.dose} onChange={(e) => setRx(i, "dose", e.target.value)} data-testid={`rx-dose-${i}`} />
                  <input placeholder="Frequency" className={`${input} col-span-2`} value={r.frequency} onChange={(e) => setRx(i, "frequency", e.target.value)} />
                  <input placeholder="Duration" className={`${input} col-span-2`} value={r.duration} onChange={(e) => setRx(i, "duration", e.target.value)} />
                  <input placeholder="Instructions" className={`${input} col-span-2`} value={r.instructions} onChange={(e) => setRx(i, "instructions", e.target.value)} />
                  <button onClick={() => removeRx(i)} className="col-span-1 text-[#D93838] hover:bg-[#FEE2E2] rounded-sm p-2 flex justify-center"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <label className={label}>Advice / Instructions</label>
              <textarea rows={3} className={input} value={rxAdvice} onChange={(e) => setRxAdvice(e.target.value)} />
            </div>
            <button onClick={saveRx} data-testid="save-rx-btn" className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#0F4C3A] text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-[#16654F]">
              <Save className="w-4 h-4" /> Save prescription
            </button>
          </div>

          <div className="bg-white border border-[#E5E5E2] rounded-md p-6">
            <h2 className="font-display text-lg font-bold tracking-tight mb-4">Past Prescriptions</h2>
            <ul className="space-y-3 max-h-[600px] overflow-auto">
              {prescriptions.length === 0 && <li className="text-sm text-[#52525B]">No prescriptions yet.</li>}
              {prescriptions.map((rx) => (
                <li key={rx.id} className="border border-[#E5E5E2] rounded-sm p-3">
                  <div className="flex justify-between items-start mb-1">
                    <div className="text-xs text-[#52525B] font-mono-num">{new Date(rx.created_at).toLocaleString("en-IN")}</div>
                    <button onClick={() => printPrescription(rx, patient)} className="text-xs px-2 py-0.5 border border-[#E5E5E2] rounded-sm hover:bg-[#F4F4F5] inline-flex items-center gap-1" data-testid={`print-rx-${rx.id}`}>
                      <Printer className="w-3 h-3" /> Print
                    </button>
                  </div>
                  <ul className="text-sm">
                    {rx.items.map((it, idx) => (
                      <li key={idx} className="py-0.5">
                        <b>{it.drug}</b> · {it.dose} · {it.frequency} · {it.duration}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === "plans" && <TreatmentPlans patientId={pid} patientName={patient.full_name} />}

      {tab === "history" && (
        <div className="bg-white border border-[#E5E5E2] rounded-md p-6">
          <h2 className="font-display text-lg font-bold tracking-tight mb-4">Case sheet history</h2>
          {caseSheets.length === 0 && <div className="text-sm text-[#52525B]">No case sheets recorded yet.</div>}
          <div className="space-y-3">
            {caseSheets.map((c) => (
              <div key={c.id} className="border border-[#E5E5E2] rounded-sm p-4">
                <div className="text-xs text-[#52525B] font-mono-num mb-2">{new Date(c.created_at).toLocaleString("en-IN")}</div>
                {c.chief_complaint && <div className="text-sm"><b>Complaint:</b> {c.chief_complaint}</div>}
                {c.diagnosis && <div className="text-sm"><b>Diagnosis:</b> {c.diagnosis}</div>}
                {c.notes && <div className="text-sm mt-1 text-[#52525B]">{c.notes}</div>}
                {c.findings?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {c.findings.map((f) => (
                      <span key={f.tooth} className="text-[10px] font-mono-num px-1.5 py-0.5 rounded-sm border" style={{ borderColor: CONDITIONS[f.condition]?.border, color: CONDITIONS[f.condition]?.border }}>
                        {f.tooth} · {CONDITIONS[f.condition]?.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
